/**
 * Default Column Layouts
 * 
 * Defines default column arrangements for different page types
 * Can be used as templates for users to choose from
 */

// Widget metadata (titles, icons, descriptions)
export const WIDGET_METADATA = {
  // Internal profile widgets
  overview: {
    title: 'Overview',
    icon: 'LayoutDashboard',
    description: 'Quick summary and key stats'
  },
  employment: {
    title: 'Employment',
    icon: 'Briefcase',
    description: 'Work history and experience'
  },
  education: {
    title: 'Education',
    icon: 'GraduationCap',
    description: 'Academic background'
  },
  compensation: {
    title: 'Compensation',
    icon: 'Coins',
    description: 'Salary and compensation details'
  },
  location: {
    title: 'Location',
    icon: 'MapPin',
    description: 'Location preferences and remote work'
  },
  documents: {
    title: 'Documents',
    icon: 'FileText',
    description: 'Resumes, certificates, and files'
  },
  activity: {
    title: 'Activity Log',
    icon: 'History',
    description: 'Complete change history'
  },

  // Client profile widgets
  'contacts-links': {
    title: 'Contacts & Links',
    icon: 'Phone',
    description: 'Emails, phones, LinkedIn, and other links'
  },
  'candidate-tracking': {
    title: 'Candidate Tracking',
    icon: 'MapPin',
    description: 'Locations, referral source, and relationships'
  },
  'compensation': {
    title: 'Compensation',
    icon: 'Coins',
    description: 'Current and expected compensation details'
  },
  'preferences': {
    title: 'Preferences',
    icon: 'Settings',
    description: 'Remote, relocation, and contact preferences'
  },
  'custom-fields': {
    title: 'Custom Fields',
    icon: 'Tag',
    description: 'Tags and custom data fields'
  },
  'positions': {
    title: 'Positions',
    icon: 'Briefcase',
    description: 'Employment history and experience'
  },
  'education': {
    title: 'Education & Certifications',
    icon: 'GraduationCap',
    description: 'Academic background and certifications'
  },
  'pipelines': {
    title: 'Searches',
    icon: 'Briefcase',
    description: 'Searches, pool status, and interview stages'
  },
  'documents': {
    title: 'Documents',
    icon: 'FileText',
    description: 'Client-uploaded documents'
  },
  'activity': {
    title: 'Activity',
    icon: 'History',
    description: 'Profile change history and updates'
  },

  // ── Firm profile widgets ──
  'firm-about': {
    title: 'About',
    icon: 'Building2',
    description: 'Firm description and industry'
  },
  'firm-key-facts': {
    title: 'Key Facts',
    icon: 'ListChecks',
    description: 'AUM, employees, payout, bonus date and other core facts'
  },
  'firm-offices': {
    title: 'Offices',
    icon: 'MapPin',
    description: 'Office locations'
  },
  'firm-enrichment': {
    title: 'LinkedIn Data',
    icon: 'Sparkles',
    description: 'Specialties, stock, funding and affiliated companies'
  },
  'firm-notes': {
    title: 'Notes',
    icon: 'StickyNote',
    description: 'Internal notes'
  },
  'firm-notes-log': {
    title: 'Communication Log',
    icon: 'MessageSquare',
    description: 'Time-stamped notes and interactions'
  },
  'firm-pipelines': {
    title: 'Pipelines',
    icon: 'GitBranch',
    description: 'Recruitment pipelines for this firm'
  },
  'firm-documents': {
    title: 'Documents',
    icon: 'FileText',
    description: 'Uploaded files and documents'
  },
  'firm-tech-stack': {
    title: 'Tech Stack',
    icon: 'Cpu',
    description: 'Execution type, languages, and data sets'
  }
};

// Default column layouts
export const DEFAULT_COLUMN_LAYOUTS = {
  // Internal candidate profile
  candidate_profile: {
    default: {
      name: 'Default Layout',
      description: 'Full-featured layout with all sections',
      columns: [
        { id: 'col-1', widgetIds: ['contacts-links', 'preferences'] },
        { id: 'col-2', widgetIds: ['candidate-tracking', 'compensation'] },
        { id: 'col-3', widgetIds: ['positions', 'education'] },
        { id: 'col-4', widgetIds: ['pipelines', 'custom-fields', 'documents', 'activity'] }
      ]
    },
    compact: {
      name: 'Compact Layout',
      description: '3-column condensed view',
      columns: [
        { id: 'col-1', widgetIds: ['contacts-links', 'candidate-tracking', 'preferences'] },
        { id: 'col-2', widgetIds: ['compensation', 'positions', 'education'] },
        { id: 'col-3', widgetIds: ['pipelines', 'custom-fields', 'documents', 'activity'] },
        { id: 'col-4', widgetIds: [] }
      ]
    },
    detailed: {
      name: 'Detailed Layout',
      description: 'Spread across all columns',
      columns: [
        { id: 'col-1', widgetIds: ['contacts-links', 'preferences'] },
        { id: 'col-2', widgetIds: ['positions'] },
        { id: 'col-3', widgetIds: ['education', 'candidate-tracking', 'compensation'] },
        { id: 'col-4', widgetIds: ['pipelines', 'custom-fields', 'documents', 'activity'] }
      ]
    }
  },

  // Client candidate profile
  client_candidate_profile: {
    default: {
      name: 'Default Layout',
      description: 'Full-featured layout with all sections',
      columns: [
        { id: 'col-1', widgetIds: ['contacts-links', 'preferences'] },
        { id: 'col-2', widgetIds: ['candidate-tracking', 'compensation'] },
        { id: 'col-3', widgetIds: ['positions', 'education'] },
        { id: 'col-4', widgetIds: ['pipelines', 'custom-fields', 'documents', 'activity'] }
      ]
    },
    compact: {
      name: 'Compact Layout',
      description: '3-column condensed view',
      columns: [
        { id: 'col-1', widgetIds: ['contacts-links', 'candidate-tracking', 'preferences'] },
        { id: 'col-2', widgetIds: ['compensation', 'positions', 'education'] },
        { id: 'col-3', widgetIds: ['pipelines', 'custom-fields', 'documents', 'activity'] },
        { id: 'col-4', widgetIds: [] }
      ]
    },
    focused: {
      name: 'Experience-Focused Layout',
      description: 'Emphasizes career history',
      columns: [
        { id: 'col-1', widgetIds: ['contacts-links', 'preferences'] },
        { id: 'col-2', widgetIds: ['positions'] },
        { id: 'col-3', widgetIds: ['education', 'candidate-tracking', 'compensation'] },
        { id: 'col-4', widgetIds: ['pipelines', 'custom-fields', 'documents', 'activity'] }
      ]
    }
  },

  // Firm profile
  firm_profile: {
    default: {
      name: 'Default Layout',
      description: 'Firm overview with key facts alongside profile content',
      columns: [
        { id: 'col-1', widgetIds: ['firm-about', 'firm-tech-stack'] },
        { id: 'col-2', widgetIds: ['firm-offices', 'firm-enrichment', 'firm-pipelines'] },
        { id: 'col-3', widgetIds: ['firm-notes-log', 'firm-documents'] },
        { id: 'col-4', widgetIds: ['firm-key-facts', 'firm-notes'] }
      ]
    },
    compact: {
      name: 'Compact Layout',
      description: 'Condensed two-column view',
      columns: [
        { id: 'col-1', widgetIds: ['firm-about', 'firm-offices', 'firm-tech-stack'] },
        { id: 'col-2', widgetIds: ['firm-key-facts', 'firm-notes-log', 'firm-pipelines', 'firm-documents', 'firm-notes'] },
        { id: 'col-3', widgetIds: ['firm-enrichment'] },
        { id: 'col-4', widgetIds: [] }
      ]
    }
  }
};

// Helper function to get default layout
export const getDefaultLayout = (pageType, templateName = 'default') => {
  const layouts = DEFAULT_COLUMN_LAYOUTS[pageType];
  if (!layouts) return null;
  
  const template = layouts[templateName] || layouts.default;
  return template ? template.columns : null;
};

// Get all available templates for a page type
export const getAvailableTemplates = (pageType) => {
  const layouts = DEFAULT_COLUMN_LAYOUTS[pageType];
  if (!layouts) return [];
  
  return Object.entries(layouts).map(([key, template]) => ({
    id: key,
    name: template.name,
    description: template.description
  }));
};
